import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { selectDocViewerMedia } from '../../../lib/doc-viewer-presenter';
import type { MediaCollection } from '../../../lib/media-collection';
import { IconButton, Typography } from '../ui';
import { DocErrorState } from './DocViewerStates';
import styles from './MediaCollectionBody.module.css';

export function MediaCollectionBody({ collection, requestId, onFirstVideoPlay }: {
  collection: MediaCollection;
  requestId: number;
  onFirstVideoPlay: () => void;
}) {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const reported = useRef(false);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const { items, index } = collection;
  const [shown, setShown] = useState(index);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (shown !== index) {
    setShown(index);
    setFailed(false);
    setAttempt(0);
  }
  const item = items[index];
  const rtl = i18n.dir() === 'rtl';
  const select = (at: number) => selectDocViewerMedia(requestId, at);
  const position = t('wins.reel.count', { index: index + 1, total: items.length });

  useEffect(() => {
    root.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    reported.current = false;
  }, [index]);

  useEffect(() => {
    const player = video.current;
    const pause = () => { if (document.hidden) player?.pause(); };
    document.addEventListener('visibilitychange', pause);
    return () => {
      document.removeEventListener('visibilitychange', pause);
      player?.pause();
    };
  }, [index, attempt]);

  return (
    <div ref={root} className={styles.root} tabIndex={0} data-testid="media-collection"
      onKeyDown={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('video, input, textarea, select') || target.isContentEditable || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          event.stopPropagation();
          select(index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
        }
      }}>
      <div className={styles.navigation}>
        <IconButton aria-label={t('calendar.toolbar.previous')} disabled={index === 0} onClick={() => select(index - 1)}>
          {rtl ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </IconButton>
        <Typography variant="p2" component="span" aria-live="polite" aria-atomic="true">{position}</Typography>
        <IconButton aria-label={t('calendar.toolbar.next')} disabled={index === items.length - 1} onClick={() => select(index + 1)}>
          {rtl ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </IconButton>
      </div>
      <div className={styles.media}
        onPointerDown={(event) => {
          pointer.current = event.pointerType === 'touch' && !(event.target as HTMLElement).closest('video, button')
            ? { x: event.clientX, y: event.clientY } : null;
        }}
        onPointerCancel={() => { pointer.current = null; }}
        onPointerUp={(event) => {
          const start = pointer.current;
          pointer.current = null;
          if (!start) return;
          const dx = event.clientX - start.x;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(event.clientY - start.y) * 2) select(index + ((dx < 0) !== rtl ? 1 : -1));
        }}>
        {failed ? <DocErrorState onRetry={() => { setFailed(false); setAttempt(attempt + 1); }} /> : item.kind === 'image' ? (
          <img key={`${index}:${attempt}`} src={item.url} alt={item.name} className={styles.asset} onError={() => setFailed(true)} />
        ) : (
          <video key={`${index}:${attempt}`} ref={video} src={item.url} aria-label={item.name} className={styles.asset} controls playsInline preload="metadata"
            onError={() => setFailed(true)} onPlay={() => { if (!reported.current) { reported.current = true; onFirstVideoPlay(); } }} />
        )}
      </div>
    </div>
  );
}
